import { formatIsoWithOffset, isValidRange } from '../../SummaryPage/summaryPeriods';
import { countRu, DAY_FORMS } from '../../../utils/pluralRu';
import { applyScopeParams, parseScopeParams } from '../../../utils/scopeSelection';
import { CONTROL_TABLE, applyTableParams, parseTableParams } from '../../../utils/analyticsTableParams';

export const CONTROL_PERIODS = [1, 7, 30];
const DAY_MS = 86400000;

/**
 * ТЗ V5 №19: считается только применённый период. Пресет — скользящий
 * интервал to=now, from = to − N дней; границы фиксируются в момент применения.
 */
export const buildControlRange = (days, now = new Date()) => ({
  from: formatIsoWithOffset(new Date(now.getTime() - days * DAY_MS)),
  to: formatIsoWithOffset(now),
});

const CONTROL_STATE = {
  ONLINE: { label: 'Онлайн', tone: 'online' },
  OFFLINE: { label: 'Оффлайн', tone: 'offline' },
  STOPPED: { label: 'Остановлена вручную', tone: 'stopped' },
};

/**
 * FE-FIX21: статус строки берётся из backend-поля control_status — он и есть
 * авторитетное состояние контроля. Комбинация desired_running +
 * connection_status остаётся только фолбэком для ответов без control_status.
 */
export const describeCameraState = (item) => {
  const byStatus = CONTROL_STATE[item?.control_status];
  if (byStatus) return byStatus;
  if (!item?.desired_running) return CONTROL_STATE.STOPPED;
  return item?.connection_status === 'ONLINE' ? CONTROL_STATE.ONLINE : CONTROL_STATE.OFFLINE;
};

export const periodLabel = (days) => countRu(days, DAY_FORMS);

/*
 * SCOPE-MULTI01 для «Контроля» (ТЗ v4 §22, FE-FIX19).
 *
 * Состояние экрана — scope + период таблицы — живёт в URL /profile?tab=control,
 * то есть отдельно от «Сводки»: Back/Forward восстанавливает именно состояние
 * «Контроля», а смена фильтров в «Сводке» здесь ничего не меняет.
 *
 * period: '1' | '7' | '30' | 'custom'. Для custom границы берутся из URL как
 * timezone-aware ISO — тот же формат, что и в «Сводке».
 */
export const CONTROL_CUSTOM_PERIOD = 'custom';

export const CONTROL_PERIOD_OPTIONS = [
  ...CONTROL_PERIODS.map((days) => ({ id: String(days), label: periodLabel(days), days })),
  { id: CONTROL_CUSTOM_PERIOD, label: 'Произвольный', days: null },
];

export const CONTROL_DEFAULT_PERIOD = '7';

/*
 * ТЗ V5 №19: вход без периода в URL возвращает последний применённый период
 * «Контроля», иначе 7 дней. Хранится только явное «Применить».
 */
const PERIOD_STORAGE_KEY = 'profileControl.period.v1';

export const loadSavedControlPeriod = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(PERIOD_STORAGE_KEY) || 'null');
    if (!saved || !CONTROL_PERIOD_OPTIONS.some((option) => option.id === saved.period)) return null;
    if (saved.period === CONTROL_CUSTOM_PERIOD && !isValidRange(saved.from, saved.to)) return null;
    return {
      period: saved.period,
      from: saved.period === CONTROL_CUSTOM_PERIOD ? saved.from : '',
      to: saved.period === CONTROL_CUSTOM_PERIOD ? saved.to : '',
    };
  } catch {
    return null;
  }
};

export const saveControlPeriod = ({ period, from, to }) => {
  try {
    localStorage.setItem(PERIOD_STORAGE_KEY, JSON.stringify({ period, from: from || '', to: to || '' }));
  } catch {
    // Без хранилища период просто не переживёт перезагрузку.
  }
};

export const parseControlParams = (searchParams) => {
  const period = searchParams?.get('period') || '';
  const known = CONTROL_PERIOD_OPTIONS.some((option) => option.id === period);
  return {
    scope: parseScopeParams(searchParams),
    // Период в URL главнее сохранённого: hasPeriod говорит, что он там есть.
    hasPeriod: known,
    period: known ? period : CONTROL_DEFAULT_PERIOD,
    from: searchParams?.get('from') || '',
    to: searchParams?.get('to') || '',
    // FE-FIX21: фильтры/сортировка/страница таблицы камер — те же control_*
    // параметры, что уходят в backend, поэтому URL читается как сам запрос.
    table: parseTableParams(searchParams, CONTROL_TABLE),
  };
};

export const applyControlParams = (searchParams, { scope, period, from, to, table }) => {
  const next = applyTableParams(applyScopeParams(searchParams, scope), CONTROL_TABLE, table);
  next.set('period', period);
  if (period === CONTROL_CUSTOM_PERIOD && from && to) {
    next.set('from', from);
    next.set('to', to);
  } else {
    next.delete('from');
    next.delete('to');
  }
  return next;
};

/** Произвольный период применим, только если обе границы разбираются и to > from. */
export const isControlCustomRangeValid = (params) =>
  params.period === CONTROL_CUSTOM_PERIOD && isValidRange(params.from, params.to);

export const CONTROL_INVALID_PERIOD_TEXT = 'Период задан неверно. Выберите период';

const pad = (value) => String(value).padStart(2, '0');
const formatBoundary = (iso) => {
  const date = new Date(iso);
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} `
    + `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** «01.10.2026 00:00–11.10.2026 00:00» — в поясе браузера, как в полях выбора дат. */
export const formatControlRange = (from, to) => `${formatBoundary(from)}–${formatBoundary(to)}`;

/*
 * FE-CORR-07 FE-001: переход «Обзор» → «Контроль» переносит период, который
 * пользователь видел в «Обзоре». «7 дней» и «30 дней» — те же скользящие
 * периоды «Контроля», остальное (календарные пресеты и произвольный) уходит
 * точными from/to. Scope не переносится: фильтры двух экранов независимы.
 * Пустые или битые границы передаются как есть, чтобы «Контроль» сказал о
 * них, а не подменил молча на 7 дней.
 */
const SUMMARY_PRESET_TO_CONTROL_PERIOD = { '7d': '7', '30d': '30' };

export const buildControlHref = ({ preset, from, to } = {}) => {
  const next = new URLSearchParams({ tab: 'control' });
  const period = SUMMARY_PRESET_TO_CONTROL_PERIOD[preset];
  if (period) {
    next.set('period', period);
  } else {
    next.set('period', CONTROL_CUSTOM_PERIOD);
    if (from) next.set('from', from);
    if (to) next.set('to', to);
  }
  return `/profile?${next.toString()}`;
};
